import React, { useState } from 'react';
import PropTypes from 'prop-types';

import NewsBanner, { BannerWrapper, shouldDisplayNewsBannerInBooking } from 'components/common/NewsBanner';

import { formatDate, formatTime } from 'utils/momentHelpers';
import getNormalizedLocationsByCities from 'utils/getNormalizedLocationsByCities';

import RescheduleModal from 'components/Booking-new/modules/Steps/PersonalData/Variants/components/RescheduleModal/RescheduleModal';

import { pushGTMEvent } from 'utils/gtm';

import { DS_palette as corePalette } from 'mui/base/theme/DS_Tokens/core';
import LoaderSpinner from '../../../../../elements/LoaderSpinner';

import StepHeader from '../../shared/StepHeader';
import PersonalForm from '../../shared/PersonalForm';

import { setLocalStorage } from '../../../../../../utils/setLocalStorage';

import Login from './components/Login';
import AuthToken from './components/authToken';
import UserMigrated from './components/UserMigrated';

import { StyledContainer } from './styles';
import { useUserProfile, useServiceAllowed } from './components/Login/hooks';

const VariantA = ({
  data,
  sdate,
  stime,
  service,
  stepTimeData,
  stepLocationData,
  stepPersonalData,
  rescheduleData,
  checkDuplicateBooking,
  savePersonalData,
  selectedCity,
  showNewsBanner,
  locations,
  serviceLogin,
}) => {
  const newsBannerVisible = shouldDisplayNewsBannerInBooking(data?.newsBanner, showNewsBanner);

  const normalizedCities = getNormalizedLocationsByCities(locations || []);
  const cityOptions = Object.keys(normalizedCities).map(key => ({
    label: normalizedCities[key].name,
    value: key,
  }));

  const {
    loginButtonLabel,
    successLoginMessage,
    failedLoginMessage,
    emailSignInLabel,
    emailSignInError,
    forgotPassword,
    migrationTitle,
    migrationText,
    downloadAPP,
    instructions,
    impressAppImg,
  } = serviceLogin;

  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [duplicateBookingData, setDuplicateBookingData] = useState(null);
  const handleClose = () => setOpenRescheduleModal(false);

  async function onFormSubmit(submitData) {
    // need to skip the duplicate booking check in case of rescheduling
    const shouldCheckDuplicateBooking = !rescheduleData;

    if (shouldCheckDuplicateBooking) {
      const { bookingData } = await checkDuplicateBooking(submitData);
      if (bookingData) {
        // if the latest booking found
        setOpenRescheduleModal(true);
        setDuplicateBookingData(bookingData);
        pushGTMEvent({
          eventAction: 'Reschedule Modal Displayed',
          eventCategory: 'Booking funnel',
        });
        setIsLoading(false);
        return false;
      } else {
        // in case of no previous booking found or error in service, continue to save user data

        return saveUserData(submitData);
      }
    } else {
      return saveUserData(submitData);
    }
  }

  async function saveUserData(submitData) {
    const serializedSubmitData = JSON.stringify(data);
    setLocalStorage('submitData', serializedSubmitData);

    const leadAdditionalData = { brand: data?.brand };

    if (data?.enableCitiesDropdownList && submitData?.city) {
      const cityKey = submitData?.city.toLowerCase();
      const selectedCity = normalizedCities[cityKey];
      return await savePersonalData(submitData, leadAdditionalData, selectedCity);
    }

    return await savePersonalData(submitData, leadAdditionalData);
  }

  const { allowsService, isChecking: isCheckingService } = useServiceAllowed(service.code);

  const initialScan = service.code === 'INS';
  const {
    authToken,
    setAuthToken,
    userSA,
    isLoading: isLoadingUserProfile,
    setIsLoading,
    error,
    isAuthTokenValid,
  } = useUserProfile({
    onFormSubmit,
    initialScan,
  });
  const isChecking = isCheckingService || (isLoadingUserProfile && !authToken);

  return (
    <StyledContainer data-test="styled-finalize-container">
      {!userSA.isMigrated && (
        <StepHeader
          title={data.title}
          isFreeParking={stepLocationData?.hasFreeParking}
          locationHeading={data?.enableCitiesDropdownList ? '' : stepLocationData?.heading}
          serviceName={service?.name}
          date={stepTimeData?.time && `${formatDate(sdate)} - ${formatTime(stime)}`}
        />
      )}
      {newsBannerVisible ? (
        <BannerWrapper>
          <NewsBanner newsBanner={data?.newsBanner} alert={true} />
        </BannerWrapper>
      ) : null}
      {userSA.isMigrated && (
        <UserMigrated
          migrationTitle={migrationTitle}
          migrationText={migrationText}
          downloadAPP={downloadAPP}
          instructions={instructions}
          impressAppImg={impressAppImg}
        />
      )}
      <RescheduleModal
        rescheduleHeadlineStepFinalize={data?.rescheduleHeadlineStepFinalize}
        rescheduleQuestionStepFinalize={data?.rescheduleQuestionStepFinalize}
        rescheduleLinkLabelStepFinalize={data?.rescheduleLinkLabelStepFinalize}
        open={openRescheduleModal}
        onClose={handleClose}
        aria-labelledby="modal-reschedule"
        aria-describedby="modal-reschedule"
        bookingData={duplicateBookingData}
      />
      {isChecking && <LoaderSpinner color={corePalette.blue[500]} />}

      {!isChecking && !error && authToken && isAuthTokenValid && !userSA.isMigrated && (
        <AuthToken
          bookingData={duplicateBookingData}
          openRescheduleModal={openRescheduleModal}
          setOpenRescheduleModal={setOpenRescheduleModal}
          userSA={userSA}
          successLoginMessage={successLoginMessage}
          existingAppointmentLabel={data?.rescheduleHeadlineStepFinalize}
          isLoading={isLoadingUserProfile}
        />
      )}

      {!isChecking && !allowsService && !authToken && !isAuthTokenValid && !userSA.isMigrated && (
        <Login
          setAuthToken={setAuthToken}
          emailLabel={emailSignInLabel}
          emailError={emailSignInError}
          loginButtonLabel={loginButtonLabel}
          failedLoginMessage={failedLoginMessage}
          forgotPassword={forgotPassword}
        />
      )}

      {!isChecking && allowsService && !authToken && (
        <PersonalForm
          formData={data}
          personalData={stepPersonalData}
          stepLocationDataCity={stepLocationData?.city}
          stepLocationDataClinic={stepLocationData?.heading}
          selectedCityValue={selectedCity?.name}
          onFormSubmit={onFormSubmit}
          locations={locations}
          cityOptions={cityOptions}
        />
      )}
    </StyledContainer>
  );
};

VariantA.propTypes = {
  data: PropTypes.shape({
    newsBanner: PropTypes.object,
    image: PropTypes.object,
    localImage: PropTypes.object,
    surveyAfterBooking: PropTypes.object,
    googleButtonLabel: PropTypes.string,
    iCalButtonLabel: PropTypes.string,
    locations: PropTypes.arrayOf(PropTypes.object),
    body: PropTypes.object,
  }),
};

export default VariantA;
